const DEFAULT_TIME_ZONE = 'Australia/Brisbane';

/**
 * True only for an IANA name `Intl` will actually honour.
 *
 * Site config can hold an empty string, a legacy abbreviation, or a corrupt
 * value; `Intl.DateTimeFormat` throws a RangeError on those at construction.
 * Every caller that reaches for a site timezone has to guard on this first,
 * or one bad row takes the whole page down.
 */
export function isUsableTimeZone(timeZone?: string | null): timeZone is string {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-AU', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The first candidate `Intl` will actually honour.
 *
 * Callers usually have a preference order — the site record over the device's
 * own stored zone, say — but `a || b` picks the first *non-empty* string, not
 * the first usable one. A site holding a legacy value like "AEST" would then
 * beat a perfectly good `inverter.timezone` and resolve to nothing at all.
 */
export function firstUsableTimeZone(...candidates: Array<string | null | undefined>): string | undefined {
  return candidates.find(isUsableTimeZone);
}

/**
 * Add `timeZone` to a set of `Intl.DateTimeFormatOptions`, so an existing
 * `toLocale*` call renders in the site's zone instead of the viewer's.
 *
 * This is the minimal-diff route through the codebase: `d.toLocaleTimeString()`
 * becomes `d.toLocaleTimeString(undefined, siteZoneOptions(tz))` and keeps the
 * exact format it had, only anchored to the site. Passing an options object
 * containing nothing but `timeZone` does not disturb the component defaults —
 * `toLocaleTimeString` still resolves hour/minute/second, `toLocaleDateString`
 * still resolves year/month/day.
 *
 * An absent or unusable zone returns the options untouched, so the call falls
 * back to the viewer's browser zone — the behaviour before this helper existed.
 */
export function siteZoneOptions(
  timeZone?: string | null,
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions | undefined {
  if (!isUsableTimeZone(timeZone)) return options;
  return { ...(options ?? {}), timeZone };
}

/**
 * The viewer's own browser zone — what every unqualified `toLocale*` call
 * resolves to. Used to decide whether a site-time label is worth showing:
 * when the two agree there is nothing to disambiguate.
 */
export function viewerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Whether a site time needs labelling for this viewer.
 *
 * Two zones can carry different IANA names and still show the same clock
 * — Australia/Brisbane and Australia/Lindeman never diverge — so comparing
 * the identifiers would label pages that need no labelling.
 *
 * But comparing the offset at a single instant is not enough either: a page
 * shows history, and the notice sits above a whole day or range rather than
 * one timestamp. Brisbane and Sydney read identically every August and an hour
 * apart every January, so a Brisbane viewer opening January's Sydney intervals
 * in August would get no notice at all while every time on screen was shifted.
 *
 * So the question is whether the two zones are *interchangeable*, not whether
 * they agree right now: sample a year of monthly instants and treat any
 * divergence as reason to label. Zones that genuinely never differ stay quiet.
 */
export function differsFromViewerZone(timeZone?: string | null, at: Date = new Date()): boolean {
  if (!isUsableTimeZone(timeZone)) return false;
  const viewer = viewerTimeZone();
  if (!viewer) return false;
  return zonesDiverge(timeZone, viewer, at);
}

/**
 * Whether two zones ever show different clocks in the year starting at `at`.
 *
 * Split out from `differsFromViewerZone` so the rule can be tested against two
 * named zones: the viewer half reads the host's own timezone, which a unit
 * test cannot vary. Same-named zones short-circuit; anything unusable is
 * treated as "no divergence" so a corrupt record cannot force a label on.
 */
export function zonesDiverge(a?: string | null, b?: string | null, at: Date = new Date()): boolean {
  if (!isUsableTimeZone(a) || !isUsableTimeZone(b)) return false;
  if (a === b) return false;
  try {
    const MONTH_MS = 30 * 24 * 60 * 60 * 1000;
    for (let month = 0; month <= 12; month += 1) {
      const sample = new Date(at.getTime() + month * MONTH_MS);
      if (timeZoneOffsetMs(sample, a) !== timeZoneOffsetMs(sample, b)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Parse an API timestamp into the instant it actually names.
 *
 * The backend is not consistent about offsets. `/api/inverter_actions` stamps
 * UTC with no suffix at all (which is why `parseUtcTime` exists), while
 * `/api/inverter_params` returns fully-qualified strings — a live payload has
 * `interval_time` as `2026-08-16T16:55:00+10:00` beside `last_inverter_time`
 * as `...+00:00`. Appending 'Z' unconditionally would turn the qualified form
 * into `...+10:00Z`, an Invalid Date; passing the bare form to `new Date()`
 * reads it as the viewer's local time and shifts the instant by their offset.
 * Convert the zone only when the string carries none.
 */
export function parseApiInstant(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The short zone abbreviation a site's clock is showing at `at` — "AEST",
 * "ACST", "GMT+8". Suffixed onto an owner-visible time so a number that reads
 * as authoritative says which clock it is on.
 *
 * `at` is required, and deliberately so. The abbreviation is a property of the
 * instant, not of the zone: a January Sydney interval is AEDT whatever today
 * is, and defaulting to `new Date()` made that easy to get wrong twice in this
 * change alone. Anything labelling a *range* rather than one timestamp has no
 * correct answer here — a range spanning a daylight-saving transition is not
 * describable by any single abbreviation — and should name the IANA zone.
 */
export function siteZoneAbbreviation(timeZone: string | null | undefined, at: Date): string | null {
  if (!isUsableTimeZone(timeZone)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-AU', { timeZone, timeZoneName: 'short' }).formatToParts(at);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
}

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function resolvedTimeZone(timeZone?: string): string {
  const candidate = timeZone || DEFAULT_TIME_ZONE;
  try {
    new Intl.DateTimeFormat('en-AU', { timeZone: candidate }).format();
    return candidate;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

function dateTimeParts(date: Date, timeZone: string): DateTimeParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour: value('hour'),
    minute: value('minute'),
    second: value('second'),
  };
}

function timeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = dateTimeParts(date, timeZone);
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - date.getTime();
}

export function formatSiteDateTime(value: string | Date, timeZone?: string): string {
  const normalizedValue = typeof value === 'string' && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
    ? `${value}Z`
    : value;
  const date = normalizedValue instanceof Date ? normalizedValue : new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: resolvedTimeZone(timeZone),
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

export function formatDateTimeLocal(value: Date, timeZone?: string): string {
  const parts = dateTimeParts(value, resolvedTimeZone(timeZone));
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

export function siteDateTimeToUtcIso(value: string, timeZone?: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) throw new Error('Invalid site date and time');

  const requested: DateTimeParts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: 0,
  };
  const wallTimeMs = Date.UTC(requested.year, requested.month - 1, requested.day, requested.hour, requested.minute);
  const zone = resolvedTimeZone(timeZone);
  const possibleOffsets = new Set<number>();
  for (let hours = -48; hours <= 48; hours += 6) {
    possibleOffsets.add(timeZoneOffsetMs(new Date(wallTimeMs + hours * 60 * 60 * 1000), zone));
  }

  const validInstants = [...possibleOffsets]
    .map((offset) => wallTimeMs - offset)
    .filter((instantMs) => {
      const actual = dateTimeParts(new Date(instantMs), zone);
      return (Object.keys(requested) as Array<keyof DateTimeParts>)
        .every((key) => actual[key] === requested[key]);
    });

  if (validInstants.length === 0) throw new Error('This time does not exist in the site timezone');
  if (validInstants.length > 1) throw new Error('This time occurs more than once in the site timezone');
  return new Date(validInstants[0]).toISOString();
}

export function formatSiteWallTime(value: string, timeZone?: string): string {
  if (!value) return '';
  try {
    return formatSiteDateTime(siteDateTimeToUtcIso(value, timeZone), timeZone);
  } catch {
    return 'Invalid time';
  }
}
